// src/pages/libros/LibroTabla.js
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box
} from '@mui/material';

const bs = (n) => (Number(n) || 0).toLocaleString('es-VE', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

const soloFecha = (f) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '');

const celdaCabecera = {
  bgcolor: '#26262C', color: 'white', fontWeight: 700,
  whiteSpace: 'nowrap', fontSize: '12px', lineHeight: 1.3
};
const celdaMonto = {
  color: 'white', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap', fontSize: '13px'
};

/**
 * La tabla de un libro fiscal.
 *
 * Las columnas de un libro son fijas y son muchas: hay tres alícuotas, cada una
 * con su base y su impuesto, más los exentos y los no gravados. No entran en una
 * pantalla, así que la tabla scrollea sola en horizontal y la página no.
 *
 * Sirve para ventas y para compras porque la estructura es la misma; cambian los
 * nombres de dos columnas y las del final: compras cierra con el IVA retenido,
 * y ventas con el IGTF más el IVA que le retuvieron a la empresa.
 */
export const LibroTabla = ({ renglones, totales, tipo }) => {
  const esVentas = tipo === 'ventas';
  // Ventas cierra con dos columnas, no con una: el IGTF que la empresa percibe
  // y el IVA que le retuvieron. Son cosas distintas y el contador mira las dos.
  const columnasFinales = esVentas
    ? [{ etiqueta: 'IGTF', campo: 'igtf' },
       { etiqueta: 'IVA retenido', campo: 'ivaRetenido' }]
    : [{ etiqueta: 'IVA retenido', campo: 'ivaRetenido' }];

  const montos = [
    { etiqueta: 'Total', campo: 'totalConIva' },
    { etiqueta: 'Exento', campo: 'exento' },
    { etiqueta: 'No gravado', campo: 'noGravado' },
    { etiqueta: 'Base 16%', campo: 'baseGeneral' },
    { etiqueta: 'IVA 16%', campo: 'ivaGeneral' },
    { etiqueta: 'Base 8%', campo: 'baseReducida' },
    { etiqueta: 'IVA 8%', campo: 'ivaReducida' },
    { etiqueta: 'Base 31%', campo: 'baseAdicional' },
    { etiqueta: 'IVA 31%', campo: 'ivaAdicional' },
    ...columnasFinales
  ];

  if (!renglones.length) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#131316', backgroundImage: 'none' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
          No hay movimientos en este período.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.5)' }}>
          Un libro sin movimientos es válido: hay meses sin operaciones y el libro
          se lleva igual.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ bgcolor: '#131316', backgroundImage: 'none', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1400 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={celdaCabecera}>Fecha</TableCell>
            <TableCell sx={celdaCabecera}>Documento</TableCell>
            <TableCell sx={celdaCabecera}>Nº</TableCell>
            <TableCell sx={celdaCabecera}>Nº control</TableCell>
            <TableCell sx={celdaCabecera}>{esVentas ? 'Cliente' : 'Proveedor'}</TableCell>
            <TableCell sx={celdaCabecera}>RIF</TableCell>
            {montos.map((m) => (
              <TableCell key={m.campo} sx={{ ...celdaCabecera, textAlign: 'right' }}>{m.etiqueta}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {renglones.map((r, i) => {
            const anulado = r.anulado;
            const resta = r.totalConIva < 0;
            const colorTexto = anulado ? 'rgba(255,255,255,0.4)' : (resta ? '#ef9a9a' : 'white');
            return (
              <TableRow key={`${r.numero || r.numeroFactura}-${i}`}
                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}>
                <TableCell sx={{ color: colorTexto, whiteSpace: 'nowrap', fontSize: '13px' }}>
                  {soloFecha(r.fecha)}
                </TableCell>
                <TableCell sx={{ color: colorTexto, whiteSpace: 'nowrap', fontSize: '13px' }}>
                  {r.tipoDocumento}
                  {anulado && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '11px', color: '#ef9a9a', fontWeight: 700 }}>
                      ANULADA
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ color: colorTexto, whiteSpace: 'nowrap', fontSize: '13px' }}>
                  {r.numero || r.numeroFactura}
                  {r.afecta && (
                    <Typography component="span" sx={{ ml: 0.5, fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                      → {r.afecta}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', fontSize: '13px' }}>
                  {r.numeroControl || '—'}
                </TableCell>
                <TableCell sx={{ color: colorTexto, fontSize: '13px', whiteSpace: 'nowrap' }}>
                  {r.nombreCliente || r.nombreProveedor}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', fontSize: '13px' }}>
                  {r.rifCliente || r.rifProveedor || '—'}
                </TableCell>
                {montos.map((m) => (
                  <TableCell key={m.campo} sx={{ ...celdaMonto, color: colorTexto }}>
                    {bs(r[m.campo])}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}

          <TableRow sx={{ bgcolor: 'rgba(245,166,35,0.08)' }}>
            <TableCell colSpan={6} sx={{ color: 'white', fontWeight: 700, fontSize: '13px' }}>
              TOTALES DEL PERÍODO
            </TableCell>
            {montos.map((m) => (
              <TableCell key={m.campo} sx={{ ...celdaMonto, fontWeight: 700 }}>
                {bs(totales[m.campo])}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Fila de la tabla, en el orden de las columnas, para exportar a CSV. */
export const renglonACelda = (r, esVentas) => ([
  String(r.fecha).slice(0, 10),
  r.tipoDocumento,
  r.numero || r.numeroFactura,
  r.numeroControl || '',
  r.nombreCliente || r.nombreProveedor,
  r.rifCliente || r.rifProveedor || '',
  r.totalConIva, r.exento, r.noGravado,
  r.baseGeneral, r.ivaGeneral, r.baseReducida, r.ivaReducida,
  r.baseAdicional, r.ivaAdicional,
  // Mismo orden que las columnas de la tabla: si se agrega una allá y no acá,
  // el CSV sale corrido y nadie lo nota hasta que el contador lo abre.
  ...(esVentas ? [r.igtf, r.ivaRetenido] : [r.ivaRetenido])
]);

export const CABECERA_CSV = (esVentas) => ([
  'Fecha', 'Documento', 'Numero', 'Numero de control',
  esVentas ? 'Cliente' : 'Proveedor', 'RIF',
  'Total', 'Exento', 'No gravado',
  'Base 16%', 'IVA 16%', 'Base 8%', 'IVA 8%', 'Base 31%', 'IVA 31%',
  ...(esVentas ? ['IGTF', 'IVA retenido'] : ['IVA retenido'])
]);

export default LibroTabla;
